import React, { useState, useEffect, useMemo } from 'react';
import { Politician } from '../api/models/models';
import { Committee, Subcommittee } from '../api/models/models';
import { CongressionalTransaction } from '../api/models/models';
import PoliticianListComponent from './politician-list-component';
import CommitteeOverviewComponent from './committee-overview-component';
import SubcommitteeOverviewComponent from './subcommittee-overview-component';
import CongressionalTransactionComponent from './congressional-transaction-component';
import { ToolbarComponent } from './toolbar-component';
import { congressionalApi, setTokenProvider } from '../services/congressionalApiService';
import { useAppSelector } from '../store/hooks';
import { 
    UserGroupIcon, 
    BuildingLibraryIcon, 
    BuildingOfficeIcon,
    CurrencyDollarIcon,
    ChartBarIcon,
    Bars3Icon,
    XMarkIcon
} from '@heroicons/react/24/outline';

interface CongressionalDashboardProps {
    onDataRefresh?: () => void;
}

type ActiveTab = 'overview' | 'politicians' | 'committees' | 'subcommittees' | 'transactions';

const CongressionalDashboard: React.FC<CongressionalDashboardProps> = ({
    onDataRefresh
}) => {
    const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
    const [selectedPolitician, setSelectedPolitician] = useState<Politician | null>(null);
    const [selectedCommittee, setSelectedCommittee] = useState<Committee | null>(null);
    const [selectedSubcommittee, setSelectedSubcommittee] = useState<Subcommittee | null>(null);
    const [subcommitteeFilterCommitteeId, setSubcommitteeFilterCommitteeId] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [dashboardData, setDashboardData] = useState({
        politicians: [] as Politician[],
        committees: [] as Committee[],
        transactions: [] as CongressionalTransaction[],
        summaryData: null as any,
        loading: true,
        error: null as string | null
    });
    
    const credentials = useAppSelector((state) => state.user.credentials);

    // Set up token provider
    useEffect(() => {
        setTokenProvider({
            getAccessToken: () => credentials?.accessToken || null
        });
    }, [credentials]);

    // Fetch dashboard data
    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!credentials?.accessToken) {
                setDashboardData(prev => ({ 
                    ...prev, 
                    loading: false, 
                    error: 'No access token available' 
                }));
                return;
            }
            
            try {
                setDashboardData(prev => ({ ...prev, loading: true }));
                const [politicians, committees, transactionsResponse, summaryData] = await Promise.all([
                    congressionalApi.getPoliticians({ limit: 1000 }), // Get all politicians for accurate count
                    congressionalApi.getCommittees(),
                    congressionalApi.getAllTransactions({ limit: 1000 }), // Get more transactions for the transactions tab
                    congressionalApi.getCongressionalSummary() // Get summary statistics
                ]);
                
                setDashboardData({
                    politicians,
                    committees,
                    transactions: transactionsResponse.transactions || [],
                    summaryData,
                    loading: false,
                    error: null
                });
            } catch (err) {
                setDashboardData(prev => ({
                    ...prev,
                    loading: false,
                    error: err instanceof Error ? err.message : 'Failed to fetch data'
                }));
            }
        };

        fetchDashboardData();
    }, [credentials?.accessToken, onDataRefresh]);

    // Calculate dashboard statistics
    const dashboardStats = useMemo(() => {
        const totalPoliticians = dashboardData.politicians.length;
        const totalCommittees = dashboardData.committees.length;
        const totalTransactions = dashboardData.summaryData?.totalTrades || dashboardData.transactions.length;
        const totalAssets = dashboardData.summaryData?.totalAssets || 0;
        
        // Calculate total subcommittees
        const totalSubcommittees = dashboardData.committees.reduce((sum, committee) => {
            return sum + (committee.subcommittees?.length || 0);
        }, 0);
        
        // Recent transactions (last 30 days) - fallback to client-side calculation
        let recentTransactions = 0;
        if (dashboardData.summaryData?.dateRange && dashboardData.summaryData.dateRange.length >= 2) {
            // For now, just show the visible transactions count as "recent"
            recentTransactions = Math.min(dashboardData.transactions.length, 50);
        } else {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            recentTransactions = dashboardData.transactions.filter(t => 
                t.date && new Date(t.date) >= thirtyDaysAgo
            ).length;
        }

        // Party breakdown - use client-side calculation
        const partyBreakdown = dashboardData.politicians.reduce((acc, politician) => {
            const party = politician.party.toLowerCase();
            if (party.includes('democrat')) {
                acc.democrats++;
            } else if (party.includes('republican')) {
                acc.republicans++;
            } else {
                acc.independents++;
            }
            return acc;
        }, { democrats: 0, republicans: 0, independents: 0 });

        // Transaction types breakdown - use API data if available
        const transactionBreakdown = dashboardData.summaryData?.byTransactionType ? {
            buys: dashboardData.summaryData.byTransactionType.buy || 0,
            sells: dashboardData.summaryData.byTransactionType.sell || 0,
            others: totalTransactions - (dashboardData.summaryData.byTransactionType.buy || 0) - (dashboardData.summaryData.byTransactionType.sell || 0)
        } : dashboardData.transactions.reduce((acc, transaction) => {
            const type = transaction.transactionType?.toLowerCase() || '';
            if (type.includes('buy')) {
                acc.buys++;
            } else if (type.includes('sell')) {
                acc.sells++;
            } else {
                acc.others++;
            }
            return acc;
        }, { buys: 0, sells: 0, others: 0 });

        return {
            totalPoliticians,
            totalCommittees,
            totalSubcommittees,
            totalTransactions,
            totalAssets,
            recentTransactions,
            partyBreakdown,
            transactionBreakdown,
            mostTradedAssets: dashboardData.summaryData?.mostTradedAssets || [],
            topTraders: dashboardData.summaryData?.topTraders || [],
            byYear: dashboardData.summaryData?.byYear || {}
        };
    }, [dashboardData]);

    const tabs = [
        {
            id: 'overview' as ActiveTab,
            name: 'Overview',
            icon: ChartBarIcon,
            count: null
        },
        {
            id: 'politicians' as ActiveTab,
            name: 'Politicians',
            icon: UserGroupIcon,
            count: dashboardData.politicians.length
        },
        {
            id: 'committees' as ActiveTab,
            name: 'Committees',
            icon: BuildingLibraryIcon,
            count: dashboardData.committees.length
        },
        {
            id: 'subcommittees' as ActiveTab,
            name: 'Subcommittees',
            icon: BuildingOfficeIcon,
            count: dashboardStats.totalSubcommittees
        },
        {
            id: 'transactions' as ActiveTab,
            name: 'Transactions',
            icon: CurrencyDollarIcon,
            count: dashboardStats.totalTransactions
        }
    ];

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Key Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="p-6 shadow-lg rounded-lg bg-white border border-neutral-300">
                    <div className="flex items-center">
                        <UserGroupIcon className="w-8 h-8 text-primary-600" />
                        <div className="ml-3">
                            <h3 className="text-lg font-medium text-neutral-800">Politicians</h3>
                            <p className="text-3xl font-bold text-primary-600">{dashboardStats.totalPoliticians.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 shadow-lg rounded-lg bg-white border border-neutral-300">
                    <div className="flex items-center">
                        <BuildingLibraryIcon className="w-8 h-8 text-primary-600" />
                        <div className="ml-3">
                            <h3 className="text-lg font-medium text-neutral-800">Committees</h3>
                            <p className="text-3xl font-bold text-primary-600">{dashboardStats.totalCommittees.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 shadow-lg rounded-lg bg-white border border-neutral-300">
                    <div className="flex items-center">
                        <BuildingOfficeIcon className="w-8 h-8 text-secondary-600" />
                        <div className="ml-3">
                            <h3 className="text-lg font-medium text-neutral-800">Subcommittees</h3>
                            <p className="text-3xl font-bold text-secondary-600">{dashboardStats.totalSubcommittees.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 shadow-lg rounded-lg bg-white border border-neutral-300">
                    <div className="flex items-center">
                        <CurrencyDollarIcon className="w-8 h-8 text-primary-600" />
                        <div className="ml-3">
                            <h3 className="text-lg font-medium text-neutral-800">Transactions</h3>
                            <p className="text-3xl font-bold text-primary-600">{dashboardStats.totalTransactions.toLocaleString()}</p>
                        </div>
                    </div>
                </div>

                <div className="p-6 shadow-lg rounded-lg bg-white border border-neutral-300">
                    <div className="flex items-center">
                        <ChartBarIcon className="w-8 h-8 text-purple-600" />
                        <div className="ml-3">
                            <h3 className="text-lg font-medium text-neutral-800">Assets</h3>
                            <p className="text-3xl font-bold text-purple-600">{dashboardStats.totalAssets.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Analytics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                {/* Party Breakdown */}
                <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">Party Distribution</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between min-w-0">
                            <span className="text-neutral-700 truncate pr-2 flex-shrink-0">Democrats</span>
                            <div className="flex items-center space-x-2 min-w-0 flex-1 justify-end">
                                <div className="w-12 sm:w-16 bg-neutral-200 rounded-full h-2 flex-shrink-0">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full"
                                        style={{ width: `${dashboardStats.totalPoliticians > 0 ? (dashboardStats.partyBreakdown.democrats / dashboardStats.totalPoliticians) * 100 : 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-blue-600 font-semibold text-sm flex-shrink-0">{dashboardStats.partyBreakdown.democrats}</span>
                            </div>
                        </div>
                        <div className="flex items-center justify-between min-w-0">
                            <span className="text-neutral-700 truncate pr-2 flex-shrink-0">Republicans</span>
                            <div className="flex items-center space-x-2 min-w-0 flex-1 justify-end">
                                <div className="w-12 sm:w-16 bg-neutral-200 rounded-full h-2 flex-shrink-0">
                                    <div 
                                        className="bg-red-600 h-2 rounded-full"
                                        style={{ width: `${dashboardStats.totalPoliticians > 0 ? (dashboardStats.partyBreakdown.republicans / dashboardStats.totalPoliticians) * 100 : 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-red-600 font-semibold text-sm flex-shrink-0">{dashboardStats.partyBreakdown.republicans}</span>
                            </div>
                        </div>
                        <div className="flex items-center justify-between min-w-0">
                            <span className="text-neutral-700 truncate pr-2 flex-shrink-0">Independents</span>
                            <div className="flex items-center space-x-2 min-w-0 flex-1 justify-end">
                                <div className="w-12 sm:w-16 bg-neutral-200 rounded-full h-2 flex-shrink-0">
                                    <div 
                                        className="bg-gray-600 h-2 rounded-full"
                                        style={{ width: `${dashboardStats.totalPoliticians > 0 ? (dashboardStats.partyBreakdown.independents / dashboardStats.totalPoliticians) * 100 : 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-gray-600 font-semibold text-sm flex-shrink-0">{dashboardStats.partyBreakdown.independents}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Transaction Breakdown */}
                <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">Transaction Types</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="text-neutral-700">Purchases</span>
                            <div className="flex items-center space-x-2">
                                <div className="w-16 bg-neutral-200 rounded-full h-2">
                                    <div 
                                        className="bg-green-600 h-2 rounded-full"
                                        style={{ width: `${dashboardStats.totalTransactions > 0 ? (dashboardStats.transactionBreakdown.buys / dashboardStats.totalTransactions) * 100 : 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-green-600 font-semibold text-sm">{dashboardStats.transactionBreakdown.buys.toLocaleString()}</span>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-neutral-700">Sales</span>
                            <div className="flex items-center space-x-2">
                                <div className="w-16 bg-neutral-200 rounded-full h-2">
                                    <div 
                                        className="bg-red-600 h-2 rounded-full"
                                        style={{ width: `${dashboardStats.totalTransactions > 0 ? (dashboardStats.transactionBreakdown.sells / dashboardStats.totalTransactions) * 100 : 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-red-600 font-semibold text-sm">{dashboardStats.transactionBreakdown.sells.toLocaleString()}</span>
                            </div>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-neutral-700">Others</span>
                            <div className="flex items-center space-x-2">
                                <div className="w-16 bg-neutral-200 rounded-full h-2">
                                    <div 
                                        className="bg-blue-600 h-2 rounded-full"
                                        style={{ width: `${dashboardStats.totalTransactions > 0 ? (dashboardStats.transactionBreakdown.others / dashboardStats.totalTransactions) * 100 : 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-blue-600 font-semibold text-sm">{dashboardStats.transactionBreakdown.others.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Most Traded Assets */}
                <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">Most Traded Assets</h3>
                    <div className="space-y-2">
                        {dashboardStats.mostTradedAssets.slice(0, 5).map((asset: any, index: number) => (
                            <div key={index} className="flex justify-between items-center py-1">
                                <span className="text-sm text-neutral-700 truncate pr-2">{asset.symbol || asset.name}</span>
                                <span className="text-sm font-semibold text-primary-600">{asset.totalTrades}</span>
                            </div>
                        ))}
                        {dashboardStats.mostTradedAssets.length === 0 && (
                            <p className="text-sm text-neutral-500 text-center py-4">No data available</p>
                        )}
                    </div>
                </div>

                {/* Top Traders */}
                <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">Top Traders</h3>
                    <div className="space-y-2">
                        {dashboardStats.topTraders.slice(0, 5).map((trader: any, index: number) => (
                            <div key={index} className="flex justify-between items-center py-1">
                                <span className="text-sm text-neutral-700 truncate pr-2">{trader.name}</span>
                                <span className="text-sm font-semibold text-primary-600">{trader.totalTrades}</span>
                            </div>
                        ))}
                        {dashboardStats.topTraders.length === 0 && (
                            <p className="text-sm text-neutral-500 text-center py-4">No data available</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Transactions */}
            {dashboardData.transactions.length > 0 && (
                <div className="bg-white shadow-lg rounded-lg border border-neutral-300 p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold text-neutral-900">Recent Transactions</h3>
                        <button
                            onClick={() => setActiveTab('transactions')}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                            View All →
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Politician
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Asset
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {dashboardData.transactions.slice(0, 10).map((transaction: any, index: number) => (
                                    <tr key={index} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {transaction.politician?.name || 'Unknown'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {transaction.asset}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                transaction.transactionType?.toLowerCase()?.includes('buy') 
                                                    ? 'bg-green-100 text-green-800'
                                                    : transaction.transactionType?.toLowerCase()?.includes('sell')
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-blue-100 text-blue-800'
                                            }`}>
                                                {transaction.transactionType || 'Unknown'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {transaction.amount}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'N/A'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );

    const renderContent = () => {
        switch (activeTab) {
            case 'overview':
                return renderOverview();
            case 'politicians':
                return (
                    <PoliticianListComponent
                        onPoliticianSelect={setSelectedPolitician}
                    />
                );
            case 'committees':
                return (
                    <CommitteeOverviewComponent
                        onCommitteeSelect={setSelectedCommittee}
                        onPoliticianSelect={setSelectedPolitician}
                        onSubcommitteeSelect={setSelectedSubcommittee}
                        onNavigateToSubcommittees={(committeeId) => {
                            setSubcommitteeFilterCommitteeId(committeeId || null);
                            setActiveTab('subcommittees');
                        }}
                    />
                );
            case 'subcommittees':
                return (
                    <SubcommitteeOverviewComponent
                        onSubcommitteeSelect={setSelectedSubcommittee}
                        onPoliticianSelect={setSelectedPolitician}
                        onCommitteeSelect={setSelectedCommittee}
                        onNavigateToCommittees={() => {
                            setSubcommitteeFilterCommitteeId(null); // Clear filter when going back
                            setActiveTab('committees');
                        }}
                        onClearCommitteeFilter={() => {
                            setSubcommitteeFilterCommitteeId(null); // Clear filter but stay on subcommittees view
                        }}
                        selectedCommitteeId={subcommitteeFilterCommitteeId}
                    />
                );
            case 'transactions':
                return (
                    <CongressionalTransactionComponent
                        onPoliticianSelect={setSelectedPolitician}
                        onTransactionSelect={(transaction) => {
                            console.log('Selected transaction:', transaction);
                            // Could open a modal or navigate to transaction details
                        }}
                    />
                );
            default:
                return renderOverview();
        }
    };

    // Loading state for dashboard data
    if (dashboardData.loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-4 text-xl text-neutral-600">Loading Congressional Dashboard...</p>
                </div>
            </div>
        );
    }

    // Error state for dashboard data
    if (dashboardData.error) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <div className="text-red-600 text-3xl mb-4">⚠️</div>
                    <p className="text-red-600 font-semibold text-xl">Error loading dashboard</p>
                    <p className="text-neutral-600 mt-2">{dashboardData.error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="mt-4 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen">
            {/* Mobile Header */}
            <div className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-neutral-200">
                <h1 className="text-xl font-bold text-primary-900">Congressional Data</h1>
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 rounded-md text-neutral-600 hover:bg-neutral-100"
                >
                    {sidebarOpen ? (
                        <XMarkIcon className="w-6 h-6" />
                    ) : (
                        <Bars3Icon className="w-6 h-6" />
                    )}
                </button>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Sidebar Navigation */}
                <div className={`${sidebarOpen ? 'block' : 'hidden'} lg:block w-64 bg-white border-r border-neutral-200 overflow-y-auto`}>
                    <div className="p-4">
                        <h1 className="hidden lg:block text-2xl font-bold text-primary-900 mb-6">Congressional Data</h1>
                        <nav className="space-y-2">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => {
                                            setActiveTab(tab.id);
                                            setSidebarOpen(false);
                                        }}
                                        className={`w-full flex items-center justify-between px-3 py-2 text-left rounded-lg transition-colors ${
                                            activeTab === tab.id
                                                ? 'bg-primary-100 text-primary-900 font-medium'
                                                : 'text-neutral-700 hover:bg-neutral-100'
                                        }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Icon className="w-5 h-5" />
                                            <span>{tab.name}</span>
                                        </div>
                                        {tab.count !== null && (
                                            <span className={`text-sm px-2 py-1 rounded-full ${
                                                activeTab === tab.id
                                                    ? 'bg-primary-200 text-primary-800'
                                                    : 'bg-neutral-200 text-neutral-600'
                                            }`}>
                                                {tab.count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Breadcrumb Navigation */}
                    <div className="bg-white border-b border-neutral-200 px-8 py-4">
                        <div className="flex items-center space-x-2 text-sm text-neutral-600">
                            <button 
                                onClick={() => setActiveTab('overview')}
                                className={`hover:text-primary-600 transition-colors ${activeTab === 'overview' ? 'text-primary-600 font-medium' : ''}`}
                            >
                                Congressional Data
                            </button>
                            {activeTab !== 'overview' && (
                                <>
                                    <span>/</span>
                                    <span className="text-neutral-900 font-medium capitalize">
                                        {activeTab}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-8">
                        {renderContent()}
                    </div>

                    {/* Toolbar */}
                    <div className="flex-shrink-0">
                        <ToolbarComponent
                            handleCreateShareLink={() => {}}
                            handleUpdateConversation={() => {}}
                            convo={undefined}
                            convos={[]}
                            handleConversationClick={(id) => {}}
                            handleCreateConversation={() => {}}
                            handleDeleteConversation={async (id: string) => {}}
                            supportedTickers={[]}
                            handleDeleteShareLink={(id: string) => {}}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CongressionalDashboard;
